import { z } from "zod/v4"

/**
 * Person type schema based on swagger.json
 */
export const personTypeSchema = z.union([
  z.literal("personal"),
  z.literal("company"),
])

export type TPersonType = z.infer<typeof personTypeSchema>

/**
 * Person filter type (for list endpoint)
 */
export const personFilterTypeSchema = z.union([
  z.literal("customer"),
  z.literal("supplier"),
])

export type TPersonFilterType = z.infer<typeof personFilterTypeSchema>

/**
 * Full person schema based on swagger.json GET /peoples/{id}
 */
export const personSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email().nullable(),
  phoneDDI: z.number().nullable(),
  phoneNumber: z.string().nullable(),
  personType: personTypeSchema,
  isSupplier: z.boolean(),
  isCustomer: z.boolean(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zipCode: z.string().nullable(),
  country: z.string().nullable(),
  taxId: z.string().nullable(),
  website: z.string().url().nullable(),
  notes: z.string().nullable(),
})

export type TPerson = z.infer<typeof personSchema>

/**
 * Person list item schema (simplified from GET /peoples/)
 */
export const personListItemSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email().nullable(),
  phoneDDI: z.number().nullable(),
  phoneNumber: z.string().nullable(),
  personType: personTypeSchema,
  isSupplier: z.boolean(),
  isCustomer: z.boolean(),
  balance: z.number(),
})

export type TPersonListItem = z.infer<typeof personListItemSchema>

/**
 * Create/Update person schema based on swagger.json POST/PUT /peoples/
 */
export const personFormSchema = z.object({
  firstName: z.string().min(1, "Nome é obrigatório"),
  lastName: z.string(),
  email: z.email("Email inválido").or(z.literal("")).nullable().optional(),
  phoneDDI: z.number().nullable().optional(),
  phoneNumber: z.string().nullable().optional(),
  personType: personTypeSchema,
  address: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  zipCode: z.string().nullable().optional(),
  country: z.string().nullable().optional(),
  taxId: z.string().nullable().optional(),
  website: z.url("URL inválida").or(z.literal("")).nullable().optional(),
  notes: z.string().nullable().optional(),
  isSupplier: z.boolean().default(false),
  isCustomer: z.boolean().default(false),
  organizationSlug: z.string(),
})

export type TPersonForm = z.infer<typeof personFormSchema>

/**
 * API Response schemas
 */
export const createPersonResponseSchema = z.object({
  peopleId: z.string(),
})

export type TCreatePersonResponse = z.infer<typeof createPersonResponseSchema>

export const listPeopleResponseSchema = z.object({
  total: z.number(),
  data: z.array(personListItemSchema),
})

export type TListPeopleResponse = z.infer<typeof listPeopleResponseSchema>
